/**
 * Extract a human-readable error message from an API error response.
 *
 * Handles FastAPI error shapes:
 *  - `{ detail: "string message" }` (HTTPException)
 *  - `{ detail: [{ msg: "..." }] }` (RequestValidationError)
 *  - Falls back to `err.message` or the provided fallback string.
 */
export function extractErrorMessage(err: any, fallback: string): string {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail[0]?.msg || fallback;
  }
  return err?.message || fallback;
}

/**
 * Seconds to wait from a rate-limited response, or `null` if this isn't one.
 *
 * Only a 429 carrying `Retry-After` qualifies. The channel-pairing endpoints
 * attach that header to the generate-code cooldown alone — their other 429 (the
 * daily distinct-target cap) omits it deliberately, because no countdown
 * resolves it, so it keeps the ordinary error path.
 */
export function extractRetryAfterSeconds(err: any): number | null {
  if (err?.response?.status !== 429) return null;
  const headers = err?.response?.headers;
  const raw = headers?.["retry-after"] ?? headers?.["Retry-After"];
  const seconds = Number(raw);
  return Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : null;
}
